import {
  Component,
  HostListener,
  ViewChild,
  OnInit,
  Input,
  EventEmitter,
  Output,
  ElementRef,
  OnChanges,
  SimpleChanges,
  OnDestroy
} from '@angular/core';
import { IChatInteraction } from '@common/models/Chat/IChatInteraction';
import { ChatServices } from '@app/services/chat.service';
import { IInteractionFeedback } from '@common/models/Chat/IInteractionFeedback';
import { UserRating } from '@common/enums/UserRating';
import { MessageService } from 'primeng/api';
import { DomSanitizer } from '@angular/platform-browser';
import { ChatStatus } from '@common/enums/ChatStatus';
import { ChatType } from '@common/enums/ChatType';
import { IChatResponse, IResponseSegments } from '@common/models/Chat/IChatResponse';
import { ResponseSegmentType } from '@common/enums/ResponseSegmentType';
import { ITableRow } from '@common/models/Document/ITableRow';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { ChatResponseEventType } from '@common/enums/ChatResponseEventType';
import { InteractionType } from '@common/enums/InteractionType';
import { ChatState } from '@common/enums/ChatState';
@Component({
  selector: 'app-chat-response',
  templateUrl: './chat-response.component.html',
  styleUrls: ['./chat-response.component.scss'],
  providers: [MessageService],
})
export class ChatResponseComponent implements OnInit, OnChanges, OnDestroy  {

  @ViewChild('responseText') private readonly editor: ElementRef<HTMLDivElement>;
  @ViewChild('scrollContainerResponse') private readonly scrollContainer!: ElementRef;

  // Input Prop
  @Input() showTypeWriter: boolean;
  @Input() lastInteraction: boolean;
  @Input() responseLength: number;
  @Input() chatReply: string;
  @Input() reasoningSummary: string;
  @Input() progressSummary: string;
  @Input() isStructuredChat: boolean
  @Input() interaction: IChatInteraction;
  @Input() showRegenerationButton: boolean;

  // Output Props
  @Output() scrollToBottom = new EventEmitter<boolean>();
  @Output() regenerate = new EventEmitter();

  text: string = '';
  typewriterText = '';
  currentWordIndex: number = 0;
  responseToDisply: string[] = [];
  
  showCursor: boolean = true;
  aceEditorMode: string;
  codeBlocks: string[];
  showdialog: boolean = false;
  showCodeDialog: boolean = false
  selectedChatInteraction: IChatInteraction;
  likeDialog: boolean;
  like: boolean;
  responseIndex: number = 0;
  selectedIndex: number = 0
  hideResponse: boolean = false;
  showCopied: boolean = false;
  chatImages:any[] = []
  displayModal: boolean = false;
  qecDataloader:boolean = false;
  latexDebounce = new Subject<any>();
  showReasoning: boolean = false;
  typewriterReasoningSummaryText = '';
  showProgress: boolean = false;
  typewriterProgressSummaryText = '';
  
  // Constants
  // Response Segment Type
  imageType : ResponseSegmentType = ResponseSegmentType.IMAGE
  textType : ResponseSegmentType = ResponseSegmentType.TEXT
  codeType : ResponseSegmentType = ResponseSegmentType.CODE
  // Chat Type
  qecChatType : ChatType = ChatType.QEC
  deepResearchChatType = ChatType.deepResearch
  quiltChatType = ChatType.quilt;
  // Chat Status
  chatStatusWaiting : ChatStatus = ChatStatus.Waiting
  // Interaction Type
  qaInteractionType: InteractionType = InteractionType.QA;
  // Chat State
  activeChatState: ChatState = ChatState.Active;
  showChartVisualizer: boolean = false;

  get currentResponse() {
    return this.interaction?.response?.[this.responseIndex];
  }

  constructor(
      private readonly chatservice: ChatServices,
      private readonly messageService: MessageService,
      private readonly sanitizer: DomSanitizer,
  ) {
    this.latexDebounce
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        if ((globalThis as any).MathJax) {
          (globalThis as any).MathJax.typeset();
        }
      });
   }


  ngOnInit(): void {
      if (this.chatReply && this.interaction.chatStatus === ChatStatus.Waiting) {
          this.typewriterText = this.chatReply
          this.typewriterReasoningSummaryText = this.reasoningSummary?.trim() ?? '';
          this.typewriterProgressSummaryText = this.progressSummary?.trim() ?? '';
      } else {
          if (this.interaction?.response?.length > 0) {
              this.responseIndex = this.interaction.response.length - 1;
          }
          this.typewriterEffect();
      }
      
      const chart:any = this.interaction.response[this.responseIndex]?.chart;
      if(Array.isArray(chart)){
        this.getImage(chart)
      }
      this.syncLikeState();
  }

  ngOnChanges(changes: SimpleChanges): void {

      if (changes['responseLength']) {
          this.responseIndex = this.responseLength
          if (!changes['responseLength'].firstChange &&
              changes['responseLength'].currentValue > changes['responseLength'].previousValue) {
              this.like = undefined;
          }
          this.typewriterEffect()
          this.syncLikeState();
      }
      if (changes['chatReply'] && this.interaction.chatStatus === ChatStatus.Waiting) {
          this.typewriterEffect()
      }
      if (changes['reasoningSummary'] && this.interaction.chatStatus === ChatStatus.Waiting) {
          this.typewriterReasoningSummaryText = changes['reasoningSummary'].currentValue?.trim() ?? '';
      }
      if (changes['progressSummary'] && this.interaction.chatStatus === ChatStatus.Waiting) {
          this.typewriterProgressSummaryText = changes['progressSummary'].currentValue?.trim() ?? '';
      }
      if (!this.showTypeWriter) {
          this.showCursor = false;
      }
      
      // Handle interaction changes (from polling updates)
      if (changes['interaction']) {
          // Update response index and re-render when interaction data changes
          if (this.interaction?.response?.length > 0) {
              this.responseIndex = this.interaction.response.length - 1;
          }
          this.typewriterEffect();
          
          // Handle scroll container setup when interaction status changes
          if (this.interaction?.chatStatus === ChatStatus.Waiting) {
              this.updateScrollContainer(true);
          } else {
              // Reset minHeight when status is no longer Waiting
              this.updateScrollContainer(false);
          }
      }
  }
  
  private updateScrollContainer(isWaiting: boolean): void {
      // Use setTimeout to ensure view is updated before accessing nativeElement
      setTimeout(() => {
          if (this.scrollContainer?.nativeElement) {
              const scrollContainer = this.scrollContainer.nativeElement;
              if (isWaiting) {
                  // Add Height to move last interaction up
                  scrollContainer.scrollTop = scrollContainer.scrollHeight;
                  scrollContainer.style.minHeight = `${scrollContainer.scrollHeight + 320}px`;
              } else {
                  // Reset minHeight when no longer waiting
                  scrollContainer.style.minHeight = '';
              }
          }
      });
  }

  ngOnDestroy() {
    this.revokeImages();
  }

  get chatType(){
    return this.chatservice.chatType;
  }

  get activeChat() {
    return this.chatservice.getActiveChat();
  }

  onRegenerate() {
    this.regenerate.emit();
  }


  @HostListener('document:copy', ['$event'])
  onCopy(event: ClipboardEvent) {
    const selection = globalThis.getSelection();
    if (selection.rangeCount){
      const range = selection.getRangeAt(0);
      const container = document.createElement('div');
      container.appendChild(range.cloneContents());
      let formattedText = this.htmlToText( container.innerHTML);
      if(formattedText.trim() != ""){
        event.preventDefault();
        event.clipboardData?.setData('text/plain', formattedText);
      }
    };
  }

  private htmlToText(html: string): string {
    const tempDivElement = document.createElement('div');
    tempDivElement.innerHTML = html;
     // Handle standalone <li> elements
    const standaloneListItems = tempDivElement.querySelectorAll('li');
    if (standaloneListItems.length > 0){
      standaloneListItems.forEach((item, index) => {
        item.textContent = `• ${item.textContent}`;
      });
    }else{
      // Convert UL to bulleted text and OL to numbered text
      const lists = tempDivElement.querySelectorAll('ul, ol');
      lists.forEach((list) => {
        let isOrdered = list.tagName.toLowerCase() === 'ol';
        let listItemIndex = 1;
        list.querySelectorAll('li').forEach((item) => {
          let prefix = isOrdered ? `${listItemIndex}. ` : '• ';
          item.textContent = prefix + item.textContent;
          if (isOrdered) listItemIndex++;
        });
      });
    }

  // Replace other HTML tags and return plain text
  return tempDivElement.textContent || tempDivElement.innerText || '';
  }

  private sanitizeMarkdownResponse(originalStr: string = ""): string {
    return originalStr
      .replaceAll(String.raw`\(`, String.raw`\\(`)
      .replaceAll(String.raw`\)`, String.raw`\\)`)
      .replaceAll(String.raw`\[`, String.raw`\\[`)
      .replaceAll(String.raw`\]`, String.raw`\\]`)
      .replaceAll(/\$(?=\d)/g, String.raw`\\$`)
      .replaceAll('~', String.raw`\~`);
  }

  typewriterEffect() {
      // 1. Hide response if no response and chat is responded
      if (this.interaction.response.length === 0 && !this.showTypeWriter && this.interaction.chatStatus === ChatStatus.Responded) {
          this.hideResponse = true;
      }
      // 2. Initialize response array if empty
      if (this.interaction['response'].length == 0) {
        this.interaction.response = [{ text: '' }];
      }

      // 3. Handle responseSegments (images, code, text)
      if(this.interaction.response[this.responseIndex]?.responseSegments?.length>0){
        let allImages = []
        this.interaction.response[this.responseIndex]?.responseSegments.map((e:IResponseSegments)=>{
          if(e.type === ResponseSegmentType.IMAGE){
            allImages.push(e.data)
          }else{
            allImages.push(undefined)
            if(e.type === ResponseSegmentType.CODE){
              e.data = e.data.replaceAll('```', '')
            }
            if(e.type === ResponseSegmentType.TEXT){
              e.data = this.sanitizeMarkdownResponse(e.data);
            }
          }
        })
        this.getImage(allImages)
        this.showCursor = false;

        setTimeout(() => {
          if(this.showCursor === false)
          {
          this.scrollToBottom.emit(this.showCursor);
          }
          this.latexDebounce.next(this.typewriterText);
        }, 100);
      }

      // 4. Handle Reasoning Summary
      const reasoningTextInResponse = this.interaction.response[this.responseIndex]?.responseEvents?.find(event => event.eventType === ChatResponseEventType.Reasoning);
      if(reasoningTextInResponse){
        this.typewriterReasoningSummaryText = reasoningTextInResponse.text?.trim() ?? '';
      } else {
        this.typewriterReasoningSummaryText = this.reasoningSummary?.trim() ?? '';
      }

      // 4.1. Handle Progress Summary
      const progressTextInResponse = this.interaction.response[this.responseIndex]?.responseEvents?.find(event => event.eventType === ChatResponseEventType.Progress);
      if(progressTextInResponse){
        this.typewriterProgressSummaryText = progressTextInResponse.text?.trim() ?? '';
      } else {
        this.typewriterProgressSummaryText = this.progressSummary?.trim() ?? '';
      }

      // 5. Handle plain text response
      if (this.interaction.response[this.responseIndex] && 'text' in this.interaction.response[this.responseIndex]) {
        let responseText =  this.interaction.response[this.responseIndex].text;
        // If chatReply is present and typewriter should show, use chatReply
        if (this.chatReply && this.showTypeWriter && this.lastInteraction) {
          responseText = this.chatReply;
        }
        // If typewriter effect is enabled and there is text or chart
        if (this.showTypeWriter && (responseText != '' || this.interaction.response[this.responseIndex]?.chart)) {
            // If chatReply is empty, animate word by word
            if (!this.chatReply || this.chatReply?.trim() == '') {
                this.responseToDisply = responseText.split(' ');
                if (
                    this.currentWordIndex < this.responseToDisply.length &&
                    this.responseToDisply.length > 0
                ) {
                    const currentWord =
                        this.responseToDisply[this.currentWordIndex];
                    this.typewriterText += this.sanitizeMarkdownResponse(currentWord) + ' ';

                    if (this.currentWordIndex == this.responseToDisply.length - 1) {
                        this.showCursor = false;
                    }
                    this.currentWordIndex++;
                    setTimeout(() => {
                        this.typewriterEffect();
                        if(this.showCursor === false)
                        {this.scrollToBottom.emit(this.showCursor);
                        }
                    }, 50);
                }
            } else {
                // If chatReply is present, show full text
                this.typewriterText = this.sanitizeMarkdownResponse(responseText);
                if(this.showCursor === false)
                {
                this.scrollToBottom.emit(this.showCursor);
                }
            }
        } else {
            // If typewriter effect is not enabled, show full text
            this.typewriterText = this.sanitizeMarkdownResponse(responseText);
            if(this.interaction.chatStatus === ChatStatus.Responded){
              this.showCursor = false;
            }
        }
    }

    // 7. Trigger LaTeX rendering
    this.latexDebounce.next(this.typewriterText);
  }

    containsTable(markdown: string): boolean {
      const lines = markdown['text']?.split('\n');

      // Looking for a line that starts and ends with a pipe '|'
      // and has a subsequent line that contains a hyphen '-'
      return lines?.some((line, index) => {
          return line.trim().startsWith('|') && line.trim().endsWith('|') &&
              lines[index + 1]?.includes('-');
      });
    }

    // Table Download in Chat

    convertMarkdownToCSV(markdownStr: string) {
      markdownStr['text'] = markdownStr['text'] + '\n'
      const tablePattern = /^\|(.+\|)+\s*\n\|(\s*-+\s*\|)+\s*\n(\|(.+\|)+\s*\n)*/gm;
      const tables = markdownStr['text'].match(tablePattern) || [];
      const csvStrings = tables.map(table => {
        const lines = table.trim().split('\n');
        const headers = lines[0].split('|').slice(1, -1).map(header => `"${header.trim()}"`);
        const rows = lines.slice(2).map(row =>
            row.split('|').slice(1, -1).map(cell => `"${cell.trim()}"`)
        );
        const csvData = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
        this.downloadCSV(csvData, 'Table');
      });
      return csvStrings;
    }

    downloadCSV(csvContent: string, fileName: string) {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);
    }

    // Response History for Interaction with multiple responses

    responseHistory(showolder: boolean) {
        this.showTypeWriter = false;
        if (showolder && this.responseIndex != 0) {
            this.responseIndex -= 1;
            this.typewriterEffect();
            this.syncLikeState();
        }
        if (
            !showolder &&
            this.responseIndex + this.interaction.response.length - 1
        ) {
            this.responseIndex += 1;
            this.typewriterEffect();
            this.syncLikeState();
        }
    }

  submitUserFeeback(userFeedback: {comment: string, userStarRating: number}) {
      this.showdialog = false;
      const payload: IInteractionFeedback = {
          userRating: this.likeDialog ? UserRating.Like : UserRating.Dislike,
          comments: userFeedback.comment ?? '',
          ...(userFeedback.userStarRating != null && { userStarRating: userFeedback.userStarRating })
      };
      const feedbackResponseIndex = this.selectedIndex ?? this.responseIndex ?? 0;
      this.chatservice
          .chatFeedback(
              this.selectedChatInteraction.chatId,
              this.selectedChatInteraction.interactionId,
              feedbackResponseIndex,
              payload
          )
          .subscribe({
              next: (res: IChatInteraction) => {
                  const updatedResponse = res?.response?.[feedbackResponseIndex];
                  if (updatedResponse) {
                      this.interaction.response[feedbackResponseIndex] = updatedResponse;
                      const currentFeedback = updatedResponse.interactionFeedback;
                      if (currentFeedback && currentFeedback.userRating !== UserRating.None && currentFeedback.userRating !== undefined) {
                          this.like = currentFeedback.userRating === UserRating.Like;
                      } else {
                          this.like = undefined;
                      }
                  }
                  this.messageService.clear();
                  this.messageService.add({
                      severity: 'success',
                      summary: 'Success',
                      detail: 'Feedback updated successfully',
                  });
              },
          });
  }

  closeDialog(value: boolean) {
      this.showdialog = false;
  }
  closeCodeDialog(value: boolean){
    this.showCodeDialog = false
  }
  showCode(value: boolean){
      this.selectedIndex = this.responseIndex
    this.selectedChatInteraction = this.interaction;
    this.showCodeDialog = value
  }
  feedback(like: boolean) {
      this.likeDialog = like;
      this.showdialog = true;
      this.selectedChatInteraction = this.interaction;
      this.selectedIndex = this.responseIndex;
  }
  copyCode() {
      const responseTextDiv = this.editor?.nativeElement;
      let htmlToCopy = '';
      let textToCopy = '';
      if (responseTextDiv) {
        htmlToCopy = responseTextDiv.innerHTML;
        textToCopy = this.htmlToText(htmlToCopy);
      } else {
        // fallback to previous logic if for some reason the element is not available
        if(this.chatType === ChatType.codeInterpreter){
          this.interaction.response[this.responseIndex]?.responseSegments?.map((e:IResponseSegments)=>{
            if(e.type !==ResponseSegmentType.IMAGE){
              textToCopy += e.data
            }
          })
        }else{
          textToCopy = this.interaction.response[this.responseIndex].text;
        }
        htmlToCopy = textToCopy;
      }

      // Use Clipboard API if available for rich text copy
      if (navigator.clipboard && globalThis.ClipboardItem) {
        const blobHtml = new Blob([htmlToCopy], { type: 'text/html' });
        const blobText = new Blob([textToCopy], { type: 'text/plain' });
        const data = {
          'text/html': blobHtml,
          'text/plain': blobText
        };
        navigator.clipboard.write([new globalThis.ClipboardItem(data)]).then(() => {
          this.showCopied = true;
          setTimeout(() => {
            this.showCopied = false;
          }, 1000);
        }, () => {
          // fallback to textarea method if Clipboard API fails
          this.fallbackCopy(textToCopy);
        });
      } else {
        this.fallbackCopy(textToCopy);
      }
  }

  private fallbackCopy(textToCopy: string) {
    const textarea = document.createElement('textarea');
    textarea.value = textToCopy;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy'); // NOSONAR: S1874 - fallback for older browsers without Clipboard API
    this.showCopied = true;
    setTimeout(() => {
        this.showCopied = false;
    }, 1000);
    textarea.remove();
  }

  getImage(uuidImageName:string[]){
    uuidImageName.forEach((e:string,index:number)=>{

      if(!this.chatImages[index] || this.chatImages[index] == 'NA'){
        if(e){
        this.chatservice
        .getChatImage(
            this.interaction.chatId,e)
        .subscribe({
            next: (res: ArrayBuffer) => {
                if (res) {
                      const bufferData = new Uint8Array(res['data']);
                      const fileName: string = uuidImageName[index] ?? '';
                      const mimeType = fileName.endsWith('.svg') ? 'image/svg+xml' : 'image/png';
                      const blob = new Blob([bufferData], { type: mimeType });
                      this.chatImages[index] = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob))
                }
            },error(err) {
            },
        });
      }else{
        this.chatImages[index] = 'NA'
      }
  }
    })

  }

  revokeImages(){
    this.chatImages.forEach((e:any)=>{
     URL.revokeObjectURL(e);
    })
  }

  downloadImage(event: MouseEvent): void {
    event.stopPropagation();
  }
  isArray(value: any): boolean {
    return value && Array.isArray(value);
  }

  private syncLikeState() {
    const responseFeedback = this.interaction?.response?.[this.responseIndex]?.interactionFeedback;
    const legacyFeedback = (this.interaction as any)?.interactionFeedback;
    const feedback = responseFeedback ?? legacyFeedback;
    if (feedback && feedback.userRating !== UserRating.None && feedback.userRating !== undefined) {
      this.like = feedback.userRating === UserRating.Like;
    } else {
      this.like = undefined;
    }
  }

  convertJSONtoCSV(chatResponse: IChatResponse) {
    let finalCode = chatResponse.final_code.replaceAll('\n', ' ');
    this.qecDataloader = true;

    this.chatservice.getDataBySqlCode(finalCode).subscribe({
        next: (res: ITableRow) => {
            this.qecDataloader = false;
            try {
                let response: any = res;
                if(response.data.length>0){
                  const csvData = this.convertToCSV(response.data);

                  const blob = new Blob([csvData], { type: 'text/csv' });
                  const url = globalThis.URL.createObjectURL(blob);

                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${this.interaction.interactionId}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                }
                this.messageService.clear();
                this.messageService.add({
                    severity: res.status==='Success'?'success':'error',
                    summary: typeof res.status === 'string' ? res.status : JSON.stringify(res.status),
                    detail: typeof res.message === 'string' ? res.message : JSON.stringify(res.message),
                });
            } catch (error) {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'An error occurred while processing the download.',
                });
                console.error('Error during CSV conversion or download:', error);
            }
        },
        error: () => {
            this.qecDataloader = false;
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to download data',
            });
        }
    });
}


convertToCSV(objArray: any[]): string {
    if (objArray.length === 0) return '';
    const headers = Object.keys(objArray[0]);
    const rows = objArray.map(obj => headers.map(header => obj[header]).join(','));
    return [headers.join(','), ...rows].join('\n');
}

}
